import { useCallback, useEffect, useRef, useState } from "react";
import { fileURL } from "../../../utils/fileURL";
import {
  obtenerTiendaInicioSetup,
  guardarTiendaInicioSetup,
  subirHeroImagenTiendaSetup,
  subirCarruselImagenTiendaSetup,
  guardarTiendaCarruselSetup,
} from "../../../services/usuarios_tienda/tiendaSetup";
import { obtenerCategoriasActivas } from "../../../services/usuarios_tienda/categorias";
import { getMarcas } from "../../../services/usuarios_tienda/marcas";
import { getSubcategoriasActivasPorCategoria } from "../../../services/usuarios_tienda/subcategorias";
import "./TabInicio.css";

const DEFAULTS = {
  hero_tag: "",
  hero_titulo: "",
  hero_descripcion: "",
  hero_boton_primario_texto: "",
  hero_boton_primario_mostrar: 0,
  destacados_titulo: "",
  destacados_descripcion: "",
  destacados_boton_texto: "",
  destacados_boton_mostrar: 0,
  banner_titulo: "",
  banner_descripcion: "",
  banner_texto_lateral: "",
  cta_titulo: "",
  cta_descripcion: "",
  cta_boton_texto: "",
  cta_boton_url: "",
  cta_boton_mostrar: 0,
  hero_imagen_url: "",
  hero_imagen_alt: "",
  estado: 1,
};

function mergeForm(server) {
  const s = server || {};
  const next = { ...DEFAULTS };
  Object.keys(DEFAULTS).forEach((k) => {
    if (s[k] !== undefined && s[k] !== null) next[k] = s[k];
  });
  return next;
}

function emptyEnlace() {
  return {
    tipo: "none",
    custom_url: "",
    categoria_id: "",
    subcategoria_id: "",
    marca_id: "",
  };
}

function normalizeCarruselItemsFromApi(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, i) => ({
    clientId:
      row.tienda_img_carrusel_id != null
        ? `db-${row.tienda_img_carrusel_id}`
        : `tmp-${i}-${Date.now()}`,
    tienda_img_carrusel_id: row.tienda_img_carrusel_id ?? null,
    url_img: row.url_img || "",
    enlace:
      row.enlace && typeof row.enlace === "object"
        ? {
            tipo: row.enlace.tipo || "none",
            custom_url: row.enlace.custom_url || "",
            categoria_id:
              row.enlace.categoria_id != null && row.enlace.categoria_id !== ""
                ? String(row.enlace.categoria_id)
                : "",
            subcategoria_id:
              row.enlace.subcategoria_id != null && row.enlace.subcategoria_id !== ""
                ? String(row.enlace.subcategoria_id)
                : "",
            marca_id:
              row.enlace.marca_id != null && row.enlace.marca_id !== ""
                ? String(row.enlace.marca_id)
                : "",
          }
        : emptyEnlace(),
    orden: row.orden ?? i,
  }));
}

function SetupTabInicio() {
  const [data, setData] = useState(() => ({ ...DEFAULTS }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCarrusel, setSavingCarrusel] = useState(false);
  const [msg, setMsg] = useState(null);
  const [uploadHeroBusy, setUploadHeroBusy] = useState(false);
  const [heroDragOver, setHeroDragOver] = useState(false);
  const heroFileRef = useRef(null);
  const [carrusel, setCarrusel] = useState({ activo: false, items: [] });
  const [categoriasOpt, setCategoriasOpt] = useState([]);
  const [marcasOpt, setMarcasOpt] = useState([]);
  const [subsByCat, setSubsByCat] = useState({});

  const patchCarrusel = (fn) => {
    setCarrusel((prev) => fn(prev));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    const res = await obtenerTiendaInicioSetup();
    if (res.ok && res.data) {
      setData(mergeForm(res.data));
      setCarrusel({
        activo: !!Number(res.data.carrusel_activo),
        items: normalizeCarruselItemsFromApi(res.data.carrusel_items),
      });
    } else if (res.ok && !res.data) {
      setData({ ...DEFAULTS });
      setCarrusel({ activo: false, items: [] });
    } else {
      setMsg({ type: "err", text: res.message });
      setData({ ...DEFAULTS });
      setCarrusel({ activo: false, items: [] });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [cats, marcas] = await Promise.all([
          obtenerCategoriasActivas(),
          getMarcas(),
        ]);
        if (!cancel) {
          setCategoriasOpt(Array.isArray(cats) ? cats : []);
          setMarcasOpt(Array.isArray(marcas) ? marcas : []);
        }
      } catch {
        if (!cancel) {
          setCategoriasOpt([]);
          setMarcasOpt([]);
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    const seen = new Set();
    carrusel.items.forEach((it) => {
      if (it.enlace?.tipo !== "catalogo" || !it.enlace.categoria_id) return;
      const cid = Number(it.enlace.categoria_id);
      if (!cid || seen.has(cid)) return;
      seen.add(cid);
      getSubcategoriasActivasPorCategoria(cid).then((list) => {
        setSubsByCat((prev) => {
          if (prev[cid]) return prev;
          return { ...prev, [cid]: Array.isArray(list) ? list : [] };
        });
      });
    });
  }, [carrusel.items]);

  const setField = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const enviarArchivoHero = async (file) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      setMsg({ type: "err", text: "Elegí un archivo de imagen (PNG, JPG o WEBP)." });
      return;
    }
    setUploadHeroBusy(true);
    setMsg(null);
    const res = await subirHeroImagenTiendaSetup(file);
    if (res.ok && res.data?.hero_imagen_url) {
      setField("hero_imagen_url", res.data.hero_imagen_url);
      setMsg({ type: "ok", text: res.message || "Imagen lista. Recordá guardar los cambios abajo." });
    } else {
      setMsg({ type: "err", text: res.message || "No se pudo subir la imagen." });
    }
    setUploadHeroBusy(false);
  };

  const onHeroInputChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) enviarArchivoHero(f);
  };

  // Reordena un item del carrusel (cambiar el orden: 1°, 2°, 3°…).
  const moverCarrusel = (clientId, dir) => {
    patchCarrusel((p) => {
      const idx = p.items.findIndex((row) => row.clientId === clientId);
      if (idx < 0) return p;
      const next = [...p.items];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return p;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...p, items: next };
    });
  };

  const onHeroDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHeroDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) enviarArchivoHero(f);
  };

  const onHeroDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHeroDragOver(true);
  };

  const onHeroDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setHeroDragOver(false);
    }
  };

  const onCarruselFile = async (clientId, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !String(f.type || "").startsWith("image/")) {
      setMsg({ type: "err", text: "Elegí un archivo de imagen (PNG, JPG o WEBP)." });
      return;
    }
    setMsg(null);
    const res = await subirCarruselImagenTiendaSetup(f);
    if (res.ok && res.data?.url_img) {
      patchCarrusel((p) => ({
        ...p,
        items: p.items.map((row) =>
          row.clientId === clientId ? { ...row, url_img: res.data.url_img } : row
        ),
      }));
      setMsg({
        type: "ok",
        text: res.message || "Imagen subida. Guardá el carrusel para aplicar los cambios en la tienda.",
      });
    } else {
      setMsg({ type: "err", text: res.message || "No se pudo subir la imagen." });
    }
  };

  const guardarCarrusel = async () => {
    const withImg = carrusel.items.filter((it) => (it.url_img || "").trim());
    if (carrusel.activo && withImg.length === 0) {
      setMsg({
        type: "err",
        text: "Para mostrar el carrusel agregá al menos una imagen o desactivá la opción.",
      });
      return;
    }
    if (withImg.length > 5) {
      setMsg({ type: "err", text: "Solo se permiten 5 imágenes. Quitá una antes de guardar." });
      return;
    }

    setSavingCarrusel(true);
    setMsg(null);

    const items = withImg.slice(0, 5).map((it, idx) => {
      const en = it.enlace || emptyEnlace();
      let enlace = null;
      if (en.tipo === "custom" && (en.custom_url || "").trim()) {
        enlace = { tipo: "custom", custom_url: en.custom_url.trim() };
      } else if (en.tipo === "catalogo") {
        const c = en.categoria_id ? Number(en.categoria_id) : null;
        const s = en.subcategoria_id ? Number(en.subcategoria_id) : null;
        const m = en.marca_id ? Number(en.marca_id) : null;
        if (
          (c && Number.isFinite(c)) ||
          (s && Number.isFinite(s)) ||
          (m && Number.isFinite(m))
        ) {
          enlace = {
            tipo: "catalogo",
            categoria_id: c && Number.isFinite(c) ? c : null,
            subcategoria_id: s && Number.isFinite(s) ? s : null,
            marca_id: m && Number.isFinite(m) ? m : null,
          };
        }
      }

      return {
        tienda_img_carrusel_id: it.tienda_img_carrusel_id || undefined,
        url_img: it.url_img.trim(),
        enlace,
        orden: idx,
      };
    });

    const res = await guardarTiendaCarruselSetup({
      carrusel_activo: carrusel.activo ? 1 : 0,
      items,
    });

    if (res.ok && res.data) {
      setCarrusel({
        activo: !!Number(res.data.carrusel_activo),
        items: normalizeCarruselItemsFromApi(res.data.carrusel_items),
      });
      setMsg({ type: "ok", text: res.message || "Carrusel guardado." });
    } else {
      setMsg({ type: "err", text: res.message || "No se pudo guardar el carrusel." });
    }
    setSavingCarrusel(false);
  };

  const guardar = async () => {
    setSaving(true);
    setMsg(null);
    const res = await guardarTiendaInicioSetup({
      hero_tag: data.hero_tag,
      hero_titulo: data.hero_titulo,
      hero_descripcion: data.hero_descripcion,
      hero_boton_primario_texto: data.hero_boton_primario_texto,
      hero_boton_primario_mostrar: Number(data.hero_boton_primario_mostrar) ? 1 : 0,
      destacados_titulo: data.destacados_titulo,
      destacados_descripcion: data.destacados_descripcion,
      destacados_boton_texto: data.destacados_boton_texto,
      destacados_boton_mostrar: Number(data.destacados_boton_mostrar) ? 1 : 0,
      banner_titulo: data.banner_titulo,
      banner_descripcion: data.banner_descripcion,
      banner_texto_lateral: data.banner_texto_lateral,
      cta_titulo: data.cta_titulo,
      cta_descripcion: data.cta_descripcion,
      cta_boton_texto: data.cta_boton_texto,
      cta_boton_url: data.cta_boton_url,
      cta_boton_mostrar: Number(data.cta_boton_mostrar) ? 1 : 0,
      hero_imagen_url: data.hero_imagen_url,
      hero_imagen_alt: data.hero_imagen_alt,
      estado: Number(data.estado) ? 1 : 0,
    });
    if (res.ok) {
      setMsg({ type: "ok", text: res.message || "Listo." });
      if (res.data) setData(mergeForm(res.data));
    } else setMsg({ type: "err", text: res.message });
    setSaving(false);
  };

  if (loading) {
    return <div className="tienda-setup-loading">Cargando pantalla de inicio…</div>;
  }

  return (
    <div className="tienda-setup-grid">
      <p style={{ margin: 0, fontSize: 13, color: "#64748B", fontWeight: 600, lineHeight: 1.45 }}>
        Estos textos e imágenes se muestran en la página de inicio pública de tu tienda
      </p>

      <section className="tienda-carrusel-panel">
        <div className="tienda-carrusel-panel__intro">
          <div>
            <h3 className="tienda-carrusel-panel__title">Carrusel de inicio</h3>
            <p className="tienda-carrusel-panel__desc">
              Recomendado <strong>1680×720 px</strong> (mín. 1260×540). Debajo de cada imagen configurás el
              enlace del botón <strong>Ver producto</strong>. Para sustituir una imagen, quitá el slot o subí
              otra encima. Usá las flechas <strong>↑ ↓</strong> para ordenar cuál se muestra primero, segundo, etc.
            </p>
          </div>
          <label className="tienda-carrusel-panel__toggle">
            <input
              type="checkbox"
              checked={!!carrusel.activo}
              onChange={(e) => patchCarrusel((p) => ({ ...p, activo: e.target.checked }))}
            />
            <span>Mostrar en la tienda</span>
          </label>
        </div>

        <div className="tienda-carrusel-grid">
          {Array.from({ length: 5 }).map((_, slotIdx) => {
            const it = slotIdx < carrusel.items.length ? carrusel.items[slotIdx] : null;
            if (!it) {
              return (
                <div key={`empty-${slotIdx}`} className="tienda-carrusel-card tienda-carrusel-card--empty">
                  <span className="tienda-carrusel-card__slot-num">{slotIdx + 1}</span>
                  <label className="tienda-carrusel-card__drop">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      disabled={carrusel.items.length >= 5}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (!f || !String(f.type || "").startsWith("image/")) return;
                        if (carrusel.items.length >= 5) return;
                        setMsg(null);
                        const res = await subirCarruselImagenTiendaSetup(f);
                        if (res.ok && res.data?.url_img) {
                          patchCarrusel((p) => ({
                            ...p,
                            items: [
                              ...p.items,
                              {
                                clientId: `slot-${Date.now()}`,
                                tienda_img_carrusel_id: null,
                                url_img: res.data.url_img,
                                enlace: emptyEnlace(),
                              },
                            ].slice(0, 5),
                          }));
                          setMsg({
                            type: "ok",
                            text: "Imagen lista. Configurá el enlace abajo y guardá el carrusel.",
                          });
                        } else {
                          setMsg({ type: "err", text: res.message || "No se pudo subir." });
                        }
                      }}
                    />
                    <span className="tienda-carrusel-card__drop-icon" aria-hidden>
                      +
                    </span>
                    <span className="tienda-carrusel-card__drop-text">
                      {carrusel.items.length >= 5 ? "Límite alcanzado" : "Agregar imagen"}
                    </span>
                  </label>
                </div>
              );
            }

            const en = it.enlace || emptyEnlace();
            const catId = en.categoria_id ? Number(en.categoria_id) : null;
            const subOps = catId && subsByCat[catId] ? subsByCat[catId] : [];

            return (
              <div key={it.clientId} className="tienda-carrusel-card">
                <div className="tienda-carrusel-card__head">
                  <span className="tienda-carrusel-card__slot-num">{slotIdx + 1}</span>
                  <div className="tienda-carrusel-card__order">
                    <button
                      type="button"
                      className="tienda-carrusel-card__order-btn"
                      onClick={() => moverCarrusel(it.clientId, -1)}
                      disabled={slotIdx === 0}
                      title="Subir (mostrar antes)"
                      aria-label="Subir posición"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="tienda-carrusel-card__order-btn"
                      onClick={() => moverCarrusel(it.clientId, 1)}
                      disabled={slotIdx >= carrusel.items.length - 1}
                      title="Bajar (mostrar después)"
                      aria-label="Bajar posición"
                    >
                      ↓
                    </button>
                  </div>
                  <button
                    type="button"
                    className="tienda-carrusel-card__remove"
                    onClick={() =>
                      patchCarrusel((p) => ({
                        ...p,
                        items: p.items.filter((row) => row.clientId !== it.clientId),
                      }))
                    }
                  >
                    Quitar
                  </button>
                </div>

                <div className="tienda-carrusel-card__preview">
                  {it.url_img ? (
                    <img src={fileURL(it.url_img) || it.url_img} alt="" />
                  ) : (
                    <span className="tienda-carrusel-card__preview-ph">Sin imagen</span>
                  )}
                  <label className="tienda-carrusel-card__replace">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={(e) => onCarruselFile(it.clientId, e)}
                    />
                    {it.url_img ? "Cambiar" : "Subir"}
                  </label>
                </div>

                <div className="tienda-carrusel-card__enlace">
                  <span className="tienda-carrusel-card__enlace-title">Enlace del botón</span>
                  <select
                    className="tienda-carrusel-card__select"
                    value={en.tipo}
                    onChange={(e) => {
                      const tipo = e.target.value;
                      patchCarrusel((p) => ({
                        ...p,
                        items: p.items.map((row) =>
                          row.clientId === it.clientId
                            ? {
                                ...row,
                                enlace:
                                  tipo === "none"
                                    ? emptyEnlace()
                                    : tipo === "custom"
                                      ? { ...emptyEnlace(), tipo: "custom", custom_url: en.custom_url }
                                      : {
                                          ...emptyEnlace(),
                                          tipo: "catalogo",
                                          categoria_id: en.categoria_id,
                                        },
                              }
                            : row
                        ),
                      }));
                    }}
                  >
                    <option value="none">Sin botón</option>
                    <option value="custom">Enlace personalizado</option>
                    <option value="catalogo">Catálogo (filtros)</option>
                  </select>

                  {en.tipo === "custom" && (
                    <input
                      className="tienda-carrusel-card__input"
                      value={en.custom_url || ""}
                      onChange={(e) =>
                        patchCarrusel((p) => ({
                          ...p,
                          items: p.items.map((row) =>
                            row.clientId === it.clientId
                              ? {
                                  ...row,
                                  enlace: { ...en, tipo: "custom", custom_url: e.target.value },
                                }
                              : row
                          ),
                        }))
                      }
                      placeholder="https://… o /store/…"
                    />
                  )}

                  {en.tipo === "catalogo" && (
                    <div className="tienda-carrusel-card__filters">
                      <select
                        className="tienda-carrusel-card__select"
                        value={en.categoria_id || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const cid = v ? Number(v) : null;
                          if (cid) {
                            getSubcategoriasActivasPorCategoria(cid).then((list) => {
                              setSubsByCat((prev) => ({
                                ...prev,
                                [cid]: Array.isArray(list) ? list : [],
                              }));
                            });
                          }
                          patchCarrusel((p) => ({
                            ...p,
                            items: p.items.map((row) =>
                              row.clientId === it.clientId
                                ? {
                                    ...row,
                                    enlace: {
                                      ...emptyEnlace(),
                                      tipo: "catalogo",
                                      categoria_id: v,
                                      subcategoria_id: "",
                                      marca_id: en.marca_id,
                                    },
                                  }
                                : row
                            ),
                          }));
                        }}
                      >
                        <option value="">Categoría</option>
                        {categoriasOpt.map((c) => (
                          <option key={c.categoria_producto_id} value={String(c.categoria_producto_id)}>
                            {c.nombre}
                          </option>
                        ))}
                      </select>
                      <select
                        className="tienda-carrusel-card__select"
                        value={en.subcategoria_id || ""}
                        disabled={!catId}
                        onChange={(e) =>
                          patchCarrusel((p) => ({
                            ...p,
                            items: p.items.map((row) =>
                              row.clientId === it.clientId
                                ? {
                                    ...row,
                                    enlace: {
                                      ...en,
                                      tipo: "catalogo",
                                      subcategoria_id: e.target.value,
                                    },
                                  }
                                : row
                            ),
                          }))
                        }
                      >
                        <option value="">Subcategoría</option>
                        {subOps.map((s) => {
                          const sid = s.subcategoria_id ?? s.subcategorias_id;
                          return (
                            <option key={sid} value={String(sid)}>
                              {s.nombre || `#${sid}`}
                            </option>
                          );
                        })}
                      </select>
                      <select
                        className="tienda-carrusel-card__select"
                        value={en.marca_id || ""}
                        onChange={(e) =>
                          patchCarrusel((p) => ({
                            ...p,
                            items: p.items.map((row) =>
                              row.clientId === it.clientId
                                ? {
                                    ...row,
                                    enlace: { ...en, tipo: "catalogo", marca_id: e.target.value },
                                  }
                                : row
                            ),
                          }))
                        }
                      >
                        <option value="">Marca</option>
                        {marcasOpt.map((m) => (
                          <option key={m.marca_producto_id} value={String(m.marca_producto_id)}>
                            {m.nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="tienda-carrusel-panel__foot-hint">
          En catálogo, elegí al menos categoría, subcategoría o marca. Guardá para aplicar cambios en la tienda
          pública.
        </p>

        <div className="tienda-carrusel-actions">
          <button
            type="button"
            className="tienda-setup-btn"
            disabled={savingCarrusel}
            onClick={guardarCarrusel}
          >
            {savingCarrusel ? "Guardando…" : "Guardar carrusel"}
          </button>
        </div>
      </section>

      <section className="tienda-hero-panel">
        <div className="tienda-hero-panel__intro">
          <div>
            <h3 className="tienda-hero-panel__title">Hero de la tienda</h3>
          </div>
        </div>

        <div className="tienda-hero-panel__layout">
          <div className="tienda-hero-panel__copy">
            <div className="tienda-setup-field tienda-hero-panel__field tienda-hero-panel__field--tag">
              <label>Etiqueta</label>
              <input
                maxLength={100}
                value={data.hero_tag || ""}
                onChange={(e) => setField("hero_tag", e.target.value)}
                placeholder="Ej. Novedades"
              />
            </div>
            <div className="tienda-setup-field tienda-hero-panel__field tienda-hero-panel__field--title">
              <label>Título</label>
              <input
                maxLength={220}
                value={data.hero_titulo || ""}
                onChange={(e) => setField("hero_titulo", e.target.value)}
                placeholder="Titular principal"
              />
            </div>
            <div className="tienda-setup-field tienda-hero-panel__field tienda-hero-panel__field--desc">
              <label>Descripción</label>
              <textarea
                value={data.hero_descripcion || ""}
                onChange={(e) => setField("hero_descripcion", e.target.value)}
                placeholder="Párrafo corto debajo del título"
                rows={4}
              />
            </div>

            <div className="tienda-hero-panel__cta-grid">
              <div className="tienda-setup-field tienda-hero-panel__field tienda-hero-panel__field--btn">
                <label>Botón primario</label>
                <input
                  maxLength={80}
                  value={data.hero_boton_primario_texto || ""}
                  onChange={(e) => setField("hero_boton_primario_texto", e.target.value)}
                  placeholder="Texto del botón"
                />
                <label className="tienda-setup-check tienda-hero-panel__check">
                  <input
                    type="checkbox"
                    checked={!!Number(data.hero_boton_primario_mostrar)}
                    onChange={(e) => setField("hero_boton_primario_mostrar", e.target.checked ? 1 : 0)}
                  />
                  Mostrar
                </label>
              </div>
            </div>

            <div className="tienda-setup-field tienda-hero-panel__field tienda-hero-panel__field--alt">
              <label>Texto alternativo de la imagen</label>
              <input
                maxLength={150}
                value={data.hero_imagen_alt || ""}
                onChange={(e) => setField("hero_imagen_alt", e.target.value)}
                placeholder="Breve descripción para accesibilidad"
              />
            </div>
          </div>

          <div className="tienda-hero-panel__visual">
            <span className="tienda-hero-panel__visual-label">Imagen del hero</span>
            <p className="tienda-hero-panel__visual-hint">
              Recomendado <strong>1600×1000 px</strong> · mín. <strong>1200×750 px</strong> · JPG,
              PNG o WEBP.
            </p>
            <input
              ref={heroFileRef}
              type="file"
              className="tienda-hero-drop__input"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={onHeroInputChange}
              aria-hidden
              tabIndex={-1}
            />
            <div
              className={`tienda-hero-drop tienda-hero-panel__drop ${heroDragOver ? "tienda-hero-drop--drag" : ""} ${
                uploadHeroBusy ? "tienda-hero-drop--busy" : ""
              }`}
              onDrop={onHeroDrop}
              onDragOver={onHeroDragOver}
              onDragLeave={onHeroDragLeave}
            >
              {fileURL(data.hero_imagen_url) ? (
                <>
                  <div
                    className="tienda-hero-drop__preview tienda-hero-panel__preview"
                    onClick={() => !uploadHeroBusy && heroFileRef.current?.click()}
                    onKeyDown={(e) => {
                      if (uploadHeroBusy) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        heroFileRef.current?.click();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Reemplazar imagen del hero"
                  >
                    <img src={fileURL(data.hero_imagen_url)} alt="" />
                  </div>
                  <p className="tienda-hero-drop__hint tienda-hero-panel__drop-hint">
                    {uploadHeroBusy ? "Subiendo…" : "Clic o arrastrá para reemplazar."}
                  </p>
                  <div className="tienda-hero-drop__actions">
                    <button
                      type="button"
                      className="tienda-hero-drop__btn tienda-hero-drop__btn--primary"
                      disabled={uploadHeroBusy}
                      onClick={() => heroFileRef.current?.click()}
                    >
                      Cambiar
                    </button>
                    <button
                      type="button"
                      className="tienda-hero-drop__btn tienda-hero-drop__btn--danger"
                      disabled={uploadHeroBusy}
                      onClick={() => setField("hero_imagen_url", "")}
                    >
                      Quitar
                    </button>
                  </div>
                </>
              ) : (
                <div
                  className="tienda-hero-panel__drop-empty"
                  onClick={() => !uploadHeroBusy && heroFileRef.current?.click()}
                  onKeyDown={(e) => {
                    if (uploadHeroBusy) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      heroFileRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Elegir imagen del hero"
                >
                  <span className="tienda-hero-panel__drop-empty-icon" aria-hidden>
                    +
                  </span>
                  <p className="tienda-hero-drop__title tienda-hero-panel__drop-title">
                    {uploadHeroBusy ? "Subiendo…" : "Subir imagen"}
                  </p>
                  <p className="tienda-hero-drop__hint">Arrastrá aquí o elegí archivo · máx. 5 MB</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="tienda-destacados-panel">
        <div className="tienda-destacados-panel__intro">
          <h3 className="tienda-destacados-panel__title">Productos destacados</h3>
          <p className="tienda-destacados-panel__lead">
            Título, texto introductorio y el botón «Ver todos» de la grilla de destacados en la tienda pública.
          </p>
        </div>
        <div className="tienda-destacados-panel__body">
          <div className="tienda-setup-field tienda-destacados-panel__field tienda-destacados-panel__field--title">
            <label>Título de la sección</label>
            <input
              maxLength={150}
              value={data.destacados_titulo || ""}
              onChange={(e) => setField("destacados_titulo", e.target.value)}
            />
          </div>
          <div className="tienda-setup-field tienda-destacados-panel__field tienda-destacados-panel__field--desc">
            <label>Descripción</label>
            <input
              maxLength={255}
              value={data.destacados_descripcion || ""}
              onChange={(e) => setField("destacados_descripcion", e.target.value)}
            />
          </div>
          <div className="tienda-destacados-panel__cta-grid">
            <div className="tienda-setup-field tienda-destacados-panel__field tienda-destacados-panel__field--btn">
              <label>Texto del botón «Ver todos»</label>
              <input
                maxLength={80}
                value={data.destacados_boton_texto || ""}
                onChange={(e) => setField("destacados_boton_texto", e.target.value)}
              />
            </div>
            <div className="tienda-setup-field tienda-destacados-panel__field tienda-destacados-panel__field--check">
              <label className="tienda-setup-check tienda-destacados-panel__check">
                <input
                  type="checkbox"
                  checked={!!Number(data.destacados_boton_mostrar)}
                  onChange={(e) => setField("destacados_boton_mostrar", e.target.checked ? 1 : 0)}
                />
                Mostrar botón «Ver todos»
              </label>
            </div>
          </div>
        </div>
      </section>

      <section className="tienda-banner-panel">
        <div className="tienda-banner-panel__intro">
          <h3 className="tienda-banner-panel__title">Banner</h3>
          <p className="tienda-banner-panel__lead">
            Encabezado, descripción y texto lateral del bloque banner en la página de inicio.
          </p>
        </div>
        <div className="tienda-banner-panel__body">
          <div className="tienda-setup-field tienda-banner-panel__field tienda-banner-panel__field--title">
            <label>Título</label>
            <input
              maxLength={150}
              value={data.banner_titulo || ""}
              onChange={(e) => setField("banner_titulo", e.target.value)}
            />
          </div>
          <div className="tienda-setup-field tienda-banner-panel__field tienda-banner-panel__field--desc">
            <label>Descripción</label>
            <input
              maxLength={255}
              value={data.banner_descripcion || ""}
              onChange={(e) => setField("banner_descripcion", e.target.value)}
            />
          </div>
          <div className="tienda-setup-field tienda-banner-panel__field tienda-banner-panel__field--side">
            <label>Texto lateral</label>
            <input
              maxLength={150}
              value={data.banner_texto_lateral || ""}
              onChange={(e) => setField("banner_texto_lateral", e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="tienda-cta-panel">
        <div className="tienda-cta-panel__intro">
          <h3 className="tienda-cta-panel__title">Llamado a la acción (CTA)</h3>
          <p className="tienda-cta-panel__lead">
            Bloque final de la página: mensaje, visibilidad del botón y URL de destino.
          </p>
        </div>
        <div className="tienda-cta-panel__body">
          <div className="tienda-setup-field tienda-cta-panel__field tienda-cta-panel__field--title">
            <label>Título</label>
            <input
              maxLength={150}
              value={data.cta_titulo || ""}
              onChange={(e) => setField("cta_titulo", e.target.value)}
            />
          </div>
          <div className="tienda-setup-field tienda-cta-panel__field tienda-cta-panel__field--desc">
            <label>Descripción</label>
            <input
              maxLength={255}
              value={data.cta_descripcion || ""}
              onChange={(e) => setField("cta_descripcion", e.target.value)}
            />
          </div>
          <div className="tienda-cta-panel__cta-grid">
            <div className="tienda-setup-field tienda-cta-panel__field tienda-cta-panel__field--btn">
              <label>Texto del botón</label>
              <input
                maxLength={80}
                value={data.cta_boton_texto || ""}
                onChange={(e) => setField("cta_boton_texto", e.target.value)}
              />
            </div>
            <div className="tienda-setup-field tienda-cta-panel__field tienda-cta-panel__field--check">
              <label className="tienda-setup-check tienda-cta-panel__check">
                <input
                  type="checkbox"
                  checked={!!Number(data.cta_boton_mostrar)}
                  onChange={(e) => setField("cta_boton_mostrar", e.target.checked ? 1 : 0)}
                />
                Mostrar botón del CTA
              </label>
            </div>
          </div>
          <div className="tienda-setup-field tienda-cta-panel__field tienda-cta-panel__field--url">
            <label>URL del botón (destino al hacer clic)</label>
            <input
              value={data.cta_boton_url || ""}
              onChange={(e) => setField("cta_boton_url", e.target.value)}
              placeholder="Ej. /store/productos o https://…"
            />
            <p className="tienda-cta-panel__hint">
              Ruta interna (empezá con /) o enlace completo (https). Si lo dejás vacío, en la tienda pública se usará el catálogo (/store/productos).
            </p>
          </div>
        </div>
      </section>

      <div className="tienda-setup-rowline">
        <h3 className="tienda-setup-h3">Publicación</h3>
        <div className="tienda-setup-field">
          <label className="tienda-setup-check">
            <input
              type="checkbox"
              checked={Number(data.estado) !== 0}
              onChange={(e) => setField("estado", e.target.checked ? 1 : 0)}
            />
            Publicar esta configuración en la tienda (desactivar oculta el contenido configurado en la página de inicio pública).
          </label>
        </div>
      </div>

      <div className="tienda-setup-actions">
        <button type="button" className="tienda-setup-btn" disabled={saving} onClick={guardar}>
          {saving ? "Guardando…" : "Guardar pantalla de inicio"}
        </button>
        {msg && (
          <p
            className={`tienda-setup-msg ${msg.type === "ok" ? "tienda-setup-msg-ok" : "tienda-setup-msg-err"}`}
          >
            {msg.text}
          </p>
        )}
      </div>
    </div>
  );
}

export default SetupTabInicio;
